/**
 * ACRES — Frontend service helpers
 * All financial-impact endpoints (redeem, approve, reject) are server-side only.
 * This file is the single source of truth for ACRES API calls from the browser.
 */
import { getSessionHeaders } from '../serverSession';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '';

async function callApi(path: string, operation: string, data: Record<string, any> = {}) {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
    body: JSON.stringify({ operation, data })
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.error || `${path} ${operation} failed`);
  return json?.data ?? json;
}

// Admin
export const acresAdmin = {
  // Templates
  listTemplates: (data?: any) => callApi('/api/admin/acres-templates', 'list_templates', data || {}),
  createTemplate: (data: any) => callApi('/api/admin/acres-templates', 'create_template', data),
  updateTemplate: (id: string, patch: any) => callApi('/api/admin/acres-templates', 'update_template', { id, patch }),
  setTemplateActive: (id: string, is_active: boolean) => callApi('/api/admin/acres-templates', 'set_active', { id, is_active }),
  deleteTemplate: (id: string) => callApi('/api/admin/acres-templates', 'delete_template', { id }),

  // Moderation
  listQueue: (data?: any) => callApi('/api/admin/acres-moderation', 'list_queue', data || {}),
  claim: (submission_id: string, ttl_minutes = 15) => callApi('/api/admin/acres-moderation', 'claim', { submission_id, ttl_minutes }),
  release: (submission_id: string) => callApi('/api/admin/acres-moderation', 'release', { submission_id }),
  approve: (submission_id: string, decision_notes?: string) => callApi('/api/admin/acres-moderation', 'approve', { submission_id, decision_notes }),
  reject: (submission_id: string, rejection_reason: string) => callApi('/api/admin/acres-moderation', 'reject', { submission_id, rejection_reason }),
  requestRevision: (submission_id: string, revision_notes: string) => callApi('/api/admin/acres-moderation', 'request_revision', { submission_id, revision_notes }),
  skip: (submission_id: string, reason?: string) => callApi('/api/admin/acres-moderation', 'skip', { submission_id, reason }),
  history: (submission_id: string) => callApi('/api/admin/acres-moderation', 'history', { submission_id }),
  myStats: () => callApi('/api/admin/acres-moderation', 'my_stats', {}),

  // Draws
  listDraws: () => callApi('/api/admin/acres-draws', 'list_draws', {}),
  getDraw: (data: any) => callApi('/api/admin/acres-draws', 'get_draw', data),
  upsertDraw: (data: any) => callApi('/api/admin/acres-draws', 'upsert_draw', data),
  setDrawStatus: (id: string, status: string) => callApi('/api/admin/acres-draws', 'set_status', { id, status }),
  upsertPrize: (data: any) => callApi('/api/admin/acres-draws', 'upsert_prize', data),
  deletePrize: (id: string) => callApi('/api/admin/acres-draws', 'delete_prize', { id }),
  listEntries: (month_year: string) => callApi('/api/admin/acres-draws', 'list_entries', { month_year }),
  runDraw: (draw_id: string, seed?: string) => callApi('/api/admin/acres-draws', 'run_draw', { draw_id, seed }),
  listWinners: (draw_id: string) => callApi('/api/admin/acres-draws', 'list_winners', { draw_id }),

  // Achievements
  listTiers: () => callApi('/api/admin/acres-achievements', 'list_tiers', {}),
  upsertTier: (data: any) => callApi('/api/admin/acres-achievements', 'upsert_tier', data),
  setTierActive: (id: string, is_active: boolean) => callApi('/api/admin/acres-achievements', 'set_active', { id, is_active }),
  listUserAwards: (data: any) => callApi('/api/admin/acres-achievements', 'list_user_awards', data),
  manualAward: (user_id: number, tier_id: string, reason?: string) => callApi('/api/admin/acres-achievements', 'manual_award', { user_id, tier_id, reason }),
  revokeAward: (award_id: string, reason: string) => callApi('/api/admin/acres-achievements', 'revoke_award', { award_id, reason }),

  // Banner
  listBannerVersions: () => callApi('/api/admin/acres-banner', 'list_versions', {}),
  getCurrentBanner: () => callApi('/api/admin/acres-banner', 'get_current', {}),
  upsertBanner: (data: any) => callApi('/api/admin/acres-banner', 'upsert_version', data),
  setBannerCurrent: (id: string) => callApi('/api/admin/acres-banner', 'set_current', { id }),
  setBannerEnabled: (id: string, is_enabled: boolean) => callApi('/api/admin/acres-banner', 'set_enabled', { id, is_enabled }),

  // Analytics
  overview: () => callApi('/api/admin/acres-analytics', 'overview', {}),
  monthlyRedemption: (months = 6) => callApi('/api/admin/acres-analytics', 'monthly_redemption', { months }),
  moderatorStats: (since?: string) => callApi('/api/admin/acres-analytics', 'moderator_stats', { since }),
  topUsers: (limit = 50) => callApi('/api/admin/acres-analytics', 'top_users', { limit }),
  recentSubmissions: (limit = 25) => callApi('/api/admin/acres-analytics', 'recent_submissions', { limit })
};

// User
export const acres = {
  listTasks: (data?: any) => callApi('/api/user/acres-tasks', 'list_eligible_for_user', data || {}),
  submitTask: (data: any) => callApi('/api/user/acres-submit', 'submit', data),
  resubmitTask: (data: any) => callApi('/api/user/acres-submit', 'resubmit', data),
  getSummary: () => callApi('/api/user/acres-summary', 'get_summary', {}),
  getLedger: (page = 1, pageSize = 50) => callApi('/api/user/acres-summary', 'get_ledger', { page, pageSize }),
  getMySubmissions: (data?: any) => callApi('/api/user/acres-summary', 'get_my_submissions', data || {}),
  getMyAchievements: () => callApi('/api/user/acres-summary', 'get_my_achievements', {}),
  getMyRedemptions: () => callApi('/api/user/acres-summary', 'get_my_redemptions', {}),
  getLeaderboard: (page = 1, pageSize = 50) => callApi('/api/user/acres-summary', 'get_leaderboard', { page, pageSize }),
  previewRedeem: (points: number) => callApi('/api/user/acres-redeem', 'preview', { points }),
  redeem: (points: number, idempotency_key?: string) => callApi('/api/user/acres-redeem', 'redeem', { points, idempotency_key }),
  getActiveBanner: () => callApi('/api/user/acres-banner', 'get_active', {}),
  dismissBanner: (banner_config_id: string) => callApi('/api/user/acres-banner', 'dismiss', { banner_config_id })
};
